import { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
  type ViewStyle,
} from "react-native";
import { Check, ChevronLeft, Lock, Search, Users } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import Svg, { Circle, Ellipse, Path } from "react-native-svg";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  colors,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import DonorRegistryHeader from "../../components/access/DonorRegistryHeader";
import { ComingSoonSheet } from "../../components/ComingSoonSheet";
import { useInterestCapture } from "../../hooks/useInterestCapture";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = NativeStackScreenProps<AccessStackParamList, "DonorRegistry">;

const COPPER_TEXTURE = require("../../../assets/images/dark-copper-texture.png");

/* ─── Skeleton placeholder ─── */

function SkeletonBlock({
  width,
  height = 14,
}: {
  width: string | number;
  height?: number;
}) {
  return (
    <View
      style={
        {
          width,
          height,
          borderRadius: (height ?? 14) / 2,
          backgroundColor: "rgba(0,0,0,0.06)",
        } as ViewStyle
      }
    />
  );
}

function SkeletonCard() {
  return (
    <View
      style={{
        ...GLASS_CARD_STYLE,
        padding: 20,
        gap: 14,
      }}
    >
      {/* Avatar + name row */}
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <View
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            backgroundColor: "rgba(0,0,0,0.06)",
          }}
        />
        <View style={{ gap: 6, flex: 1 }}>
          <SkeletonBlock width="60%" height={14} />
          <SkeletonBlock width="40%" height={10} />
        </View>
      </View>
      {/* Detail rows */}
      <View style={{ gap: 8 }}>
        <SkeletonBlock width="90%" height={10} />
        <SkeletonBlock width="75%" height={10} />
        <SkeletonBlock width="50%" height={10} />
      </View>
    </View>
  );
}

/* ─── Feature preview items ─── */

type FeatureItem = {
  icon: LucideIcon;
  title: string;
  dialogTitle: string;
  description: string;
  /** i18n key of `title`, kept stable for analytics across locales. */
  action: string;
};

function buildFeatures(t: TranslationFunction): FeatureItem[] {
  return [
    {
      icon: Search,
      title: t("access.donor_registry.feature_browse_title"),
      dialogTitle: t("access.donor_registry.feature_browse_dialog_title"),
      description: t("access.donor_registry.feature_browse_description"),
      action: "access.donor_registry.feature_browse_title",
    },
    {
      icon: Users,
      title: t("access.donor_registry.feature_match_title"),
      dialogTitle: t("access.donor_registry.feature_match_dialog_title"),
      description: t("access.donor_registry.feature_match_description"),
      action: "access.donor_registry.feature_match_title",
    },
  ];
}

function EarlyAccessIllustration() {
  return (
    <View
      style={{
        width: 58,
        height: 46,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Svg width={58} height={46} viewBox="0 0 58 46" fill="none">
        <Ellipse
          cx={29}
          cy={24}
          rx={25}
          ry={16}
          fill="rgba(255,255,255,0.13)"
        />
        <Circle
          cx={22}
          cy={21}
          r={7.5}
          fill="rgba(255,248,240,0.12)"
          stroke="rgba(255,255,255,0.82)"
          strokeWidth={1.7}
        />
        <Path
          d="M11.5 35C14.5 29.5 19.2 27.5 23.8 28.1C28.3 28.7 31.6 31.2 33.5 35"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth={1.8}
          strokeLinecap="round"
        />
        <Circle
          cx={37}
          cy={16}
          r={5.2}
          fill="rgba(255,248,240,0.12)"
          stroke="rgba(255,255,255,0.72)"
          strokeWidth={1.5}
        />
        <Path
          d="M31.5 29.5C34 25.6 37.4 24.4 40.6 24.9C43.9 25.4 46.3 27.1 47.5 30.2"
          stroke="rgba(255,255,255,0.68)"
          strokeWidth={1.5}
          strokeLinecap="round"
        />
        <Path
          d="M42.5 9.5L44.4 11.5L48.5 6.8"
          stroke="rgba(255,255,255,0.92)"
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </View>
  );
}

/* ─── Screen ─── */

export default function DonorRegistryScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("DonorRegistryScreen");
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const [earlyAccessCaptured, setEarlyAccessCaptured] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] =
    useState<FeatureItem | null>(null);
  const { capture } = useInterestCapture();
  const features = useMemo(() => buildFeatures(t), [t]);

  const handleEarlyAccessPress = useCallback(() => {
    if (earlyAccessCaptured) {
      return;
    }
    capture({
      featureId: "donor-registry",
      featureType: "product",
      featureName: "Donor Registry",
    });
    setEarlyAccessCaptured(true);
  }, [capture, earlyAccessCaptured]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab
                  ? t("you.shared.back")
                  : t("access.back"),
              ),
              "back",
            );
            handleBack();
          }}
          hitSlop={12}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 16,
          }}
        >
          <ChevronLeft size={20} color={EMBER.colors.warm} strokeWidth={2.5} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "600",
              color: EMBER.colors.warm,
            }}
          >
            {route.params?.returnTab ? t("you.shared.back") : t("access.back")}
          </Text>
        </Pressable>

        <DonorRegistryHeader />

        {/* What to expect */}
        <View style={{ flexDirection: "row", gap: 10, marginBottom: 28 }}>
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Pressable
                key={feature.title}
                onPress={() => {
                  capturePress(pressLabel(feature.title), feature.action);
                  setComingSoonFeature(feature);
                }}
                style={({ pressed }) => ({
                  ...GLASS_CARD_STYLE,
                  flex: 1,
                  minWidth: 0,
                  padding: 14,
                  gap: 10,
                  opacity: pressed ? 0.85 : 1,
                })}
              >
                <View
                  style={{
                    width: 28,
                    height: 28,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={22} color={colors.greyBrown} strokeWidth={2} />
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                    lineHeight: 17,
                  }}
                >
                  {feature.title}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textLight,
                    lineHeight: 16,
                  }}
                >
                  {feature.description}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <View
          style={{
            borderRadius: 24,
            overflow: "hidden",
            minHeight: 172,
            marginBottom: 28,
            padding: 18,
            backgroundColor: colors.deepBrown,
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.14)",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 10 },
            shadowOpacity: 0.12,
            shadowRadius: 28,
          }}
        >
          <Image
            source={COPPER_TEXTURE}
            style={{
              position: "absolute",
              width: "122%",
              height: "132%",
              left: "-11%",
              top: "-18%",
              opacity: 0.94,
            }}
            resizeMode="cover"
          />
          <View
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: "rgba(35, 12, 4, 0.18)",
            }}
          />
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
              }}
            >
              <Lock size={13} color="rgba(255,255,255,0.72)" strokeWidth={2} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: "rgba(255,255,255,0.72)",
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                }}
              >
                {t("access.donor_registry.private_preview")}
              </Text>
            </View>
            <EarlyAccessIllustration />
          </View>

          <View style={{ gap: 7, maxWidth: 380 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 21,
                fontWeight: "800",
                color: "#FFFFFF",
                lineHeight: 26,
              }}
            >
              {t("access.donor_registry.request_card_title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: "rgba(255,255,255,0.68)",
                lineHeight: 18,
              }}
            >
              {t("access.donor_registry.request_card_body")}
            </Text>
          </View>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(
                  earlyAccessCaptured
                    ? t("access.donor_registry.request_cta_done")
                    : t("access.donor_registry.request_cta"),
                ),
                "access.donor_registry.request_cta",
              );
              handleEarlyAccessPress();
            }}
            style={({ pressed }) => ({
              alignSelf: "flex-start",
              height: 44,
              minWidth: 156,
              marginTop: 18,
              paddingHorizontal: 18,
              borderRadius: 999,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              backgroundColor: earlyAccessCaptured
                ? "rgba(255,255,255,0.18)"
                : "rgba(255,255,255,0.16)",
              borderWidth: 1,
              borderColor: "rgba(255,255,255,0.24)",
              opacity: pressed ? 0.86 : 1,
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }),
            })}
          >
            {earlyAccessCaptured ? (
              <Check size={15} color="#FFFFFF" strokeWidth={2.4} />
            ) : (
              <Lock size={15} color="#FFFFFF" strokeWidth={2.2} />
            )}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "700",
                color: "#FFFFFF",
              }}
            >
              {earlyAccessCaptured
                ? t("access.donor_registry.request_cta_done")
                : t("access.donor_registry.request_cta")}
            </Text>
          </Pressable>
        </View>

        {/* Skeleton preview — what the page will look like */}
        <View style={{ gap: 10, marginBottom: 28 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: EMBER.colors.textFaint,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            {t("access.donor_registry.preview_label")}
          </Text>

          {/* Skeleton search bar */}
          <View
            style={{
              ...GLASS_CARD_STYLE,
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              padding: 14,
              paddingHorizontal: 16,
            }}
          >
            <Search size={18} color="rgba(0,0,0,0.15)" strokeWidth={2} />
            <SkeletonBlock width="70%" height={14} />
          </View>

          {/* Skeleton filter chips */}
          <View style={{ flexDirection: "row", gap: 8 }}>
            <SkeletonBlock width={80} height={28} />
            <SkeletonBlock width={65} height={28} />
            <SkeletonBlock width={90} height={28} />
          </View>

          {/* Skeleton donor cards */}
          <SkeletonCard />
          <SkeletonCard />
        </View>
      </ScrollView>

      <ComingSoonSheet
        open={comingSoonFeature !== null}
        onClose={() => setComingSoonFeature(null)}
        title={comingSoonFeature?.dialogTitle ?? ""}
        description={comingSoonFeature?.description ?? ""}
        onNotify={earlyAccessCaptured ? undefined : handleEarlyAccessPress}
      />
    </GlassScreenLayout>
  );
}
